import * as React from "react";
import { Crosshair, Loader2, MapPin } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Switch,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  APPROVAL_MODE_HELP,
  APPROVAL_MODE_LABELS,
  RECOVERY_PLAN_LABELS,
  useTeamPayPolicy,
  useUpdateAttendancePolicy,
  useUpdatePayPolicy,
  type ApprovalMode,
  type DayBasis,
  type RecoveryPlan,
  type TeamPayPolicy,
} from "@/lib/teamPay/api";
import { ErrorBanner, Field, NativeSelect } from "@/components/team-pay/shared";
import { errorMessage } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — how attendance is approved, where the centre is (for
 * check-in), and how pay is worked out. Settings → Attendance location.
 */

const MODES: ApprovalMode[] = [
  "geo_auto_approve",
  "admin_approves_each",
  "self_auto_approve",
  "admin_marks_only",
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type LocateState =
  | { kind: "idle" }
  | { kind: "locating" }
  | { kind: "error"; message: string }
  | { kind: "found"; accuracy: number };

function ToggleRow({
  label,
  helper,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  helper: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="border-border/60 flex items-start justify-between gap-4 rounded-lg border px-3.5 py-3">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="text-muted-foreground block text-xs">{helper}</span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={label} />
    </label>
  );
}

function AttendanceCard({ policy, canManage }: { policy: TeamPayPolicy; canManage: boolean }) {
  const save = useUpdateAttendancePolicy();
  const [mode, setMode] = React.useState<ApprovalMode>(policy.default_approval_mode);
  const [lat, setLat] = React.useState(policy.geofence_lat?.toString() ?? "");
  const [lng, setLng] = React.useState(policy.geofence_lng?.toString() ?? "");
  const [radius, setRadius] = React.useState(String(policy.geofence_radius_m));
  const [accuracy, setAccuracy] = React.useState(String(policy.max_accuracy_m));
  const [label, setLabel] = React.useState(policy.location_label ?? "");
  const [selfCheckIn, setSelfCheckIn] = React.useState(policy.allow_self_check_in);
  const [locate, setLocate] = React.useState<LocateState>({ kind: "idle" });
  const [error, setError] = React.useState<string | null>(null);

  const locateMe = () => {
    if (!("geolocation" in navigator)) {
      setLocate({ kind: "error", message: "This browser can't share a location." });
      return;
    }
    setLocate({ kind: "locating" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setLocate({ kind: "found", accuracy: Math.round(pos.coords.accuracy) });
      },
      (err) =>
        setLocate({
          kind: "error",
          message:
            err.code === err.PERMISSION_DENIED
              ? "Location permission was denied. Allow it in the browser and try again."
              : "Couldn't get your location. Try again near a window, or type the coordinates.",
        }),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  };

  const latN = Number(lat);
  const lngN = Number(lng);
  const hasCoords = lat !== "" && lng !== "" && Number.isFinite(latN) && Number.isFinite(lngN);
  const configured = policy.geofence_lat !== null && policy.geofence_lng !== null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({
        default_approval_mode: mode,
        allow_self_check_in: selfCheckIn,
        geofence_radius_m: Math.round(Number(radius)),
        max_accuracy_m: Math.round(Number(accuracy)),
        location_label: label.trim() || null,
        ...(hasCoords ? { geofence_lat: latN, geofence_lng: lngN } : {}),
      });
      toast.success("Attendance settings saved.");
    } catch (err) {
      setError(errorMessage(err, "Couldn't save attendance settings."));
    }
  };

  const clearLocation = async () => {
    setError(null);
    try {
      await save.mutateAsync({ clear_geofence: true });
      setLat("");
      setLng("");
      setLocate({ kind: "idle" });
      toast.success("Attendance location removed.");
    } catch (err) {
      setError(errorMessage(err, "Couldn't remove the location."));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Attendance</CardTitle>
        <CardDescription>How check-ins are approved and where the centre is.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-5">
          {error ? <ErrorBanner message={error} /> : null}

          <fieldset className="space-y-2" disabled={!canManage}>
            <legend className="mb-1 text-sm font-medium">Approving attendance</legend>
            {MODES.map((m) => (
              <label
                key={m}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-150 ease-out",
                  mode === m ? "border-primary bg-primary/[0.04]" : "border-border/60",
                )}
              >
                <input
                  type="radio"
                  name="tp-mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="accent-primary mt-1"
                />
                <span>
                  <span className="block text-sm font-medium">{APPROVAL_MODE_LABELS[m]}</span>
                  <span className="text-muted-foreground block text-xs">
                    {APPROVAL_MODE_HELP[m]}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <ToggleRow
            label="Let the team check in from the portal"
            helper="When off, only you mark attendance."
            checked={selfCheckIn}
            onChange={setSelfCheckIn}
            disabled={!canManage}
          />

          <section className="space-y-3" aria-labelledby="tp-location-heading">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3
                id="tp-location-heading"
                className="flex items-center gap-1.5 text-sm font-medium"
              >
                <MapPin aria-hidden="true" className="size-4" />
                Attendance location
              </h3>
              {canManage ? (
                <div className="flex gap-2">
                  {configured ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={save.isPending}
                      onClick={clearLocation}
                    >
                      Remove
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={locate.kind === "locating"}
                    onClick={locateMe}
                    data-testid="use-current-location"
                  >
                    {locate.kind === "locating" ? (
                      <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                    ) : (
                      <Crosshair aria-hidden="true" className="size-4" />
                    )}
                    Use my current location
                  </Button>
                </div>
              ) : null}
            </div>
            <p className="text-muted-foreground text-xs" aria-live="polite">
              {locate.kind === "found"
                ? `Got it, accurate to about ${locate.accuracy} m. Save to use it.`
                : locate.kind === "error"
                  ? locate.message
                  : configured
                    ? "Check-ins inside the circle count as at the centre."
                    : "Not set yet. Stand at the centre and tap Use my current location."}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Latitude" htmlFor="tp-lat">
                <Input
                  id="tp-lat"
                  inputMode="decimal"
                  value={lat}
                  disabled={!canManage}
                  onChange={(e) => setLat(e.target.value)}
                />
              </Field>
              <Field label="Longitude" htmlFor="tp-lng">
                <Input
                  id="tp-lng"
                  inputMode="decimal"
                  value={lng}
                  disabled={!canManage}
                  onChange={(e) => setLng(e.target.value)}
                />
              </Field>
              <Field label="Circle size (metres)" htmlFor="tp-radius" helper="25 to 5000">
                <Input
                  id="tp-radius"
                  type="number"
                  inputMode="numeric"
                  min={25}
                  max={5000}
                  value={radius}
                  disabled={!canManage}
                  onChange={(e) => setRadius(e.target.value)}
                />
              </Field>
              <Field
                label="Worst accuracy allowed (metres)"
                htmlFor="tp-accuracy"
                helper="Readings blurrier than this are refused"
              >
                <Input
                  id="tp-accuracy"
                  type="number"
                  inputMode="numeric"
                  min={10}
                  max={2000}
                  value={accuracy}
                  disabled={!canManage}
                  onChange={(e) => setAccuracy(e.target.value)}
                />
              </Field>
            </div>
            <Field label="Place name" htmlFor="tp-label">
              <Input
                id="tp-label"
                value={label}
                maxLength={200}
                disabled={!canManage}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Main centre, MG Road"
              />
            </Field>
          </section>

          {canManage ? (
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? (
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                ) : null}
                Save attendance settings
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

function PayCard({ policy, canManage }: { policy: TeamPayPolicy; canManage: boolean }) {
  const save = useUpdatePayPolicy();
  const [dayBasis, setDayBasis] = React.useState<DayBasis>(policy.day_basis);
  const [paidHolidays, setPaidHolidays] = React.useState(policy.daily_paid_holidays);
  const [plan, setPlan] = React.useState<RecoveryPlan>(policy.default_recovery_plan);
  const [emi, setEmi] = React.useState(String(policy.default_emi_months));
  const [leaveStart, setLeaveStart] = React.useState(policy.leave_year_start_month);
  const [showPay, setShowPay] = React.useState(policy.show_pay_to_team);
  const [allowLeave, setAllowLeave] = React.useState(policy.allow_leave_requests);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({
        day_basis: dayBasis,
        daily_paid_holidays: paidHolidays,
        default_recovery_plan: plan,
        default_emi_months: Math.max(1, Math.min(36, Math.round(Number(emi) || 1))),
        leave_year_start_month: leaveStart,
        show_pay_to_team: showPay,
        allow_leave_requests: allowLeave,
      });
      toast.success("Pay settings saved.");
    } catch (err) {
      setError(errorMessage(err, "Couldn't save pay settings."));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pay</CardTitle>
        <CardDescription>How salaries are counted and advances are taken back.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          {error ? <ErrorBanner message={error} /> : null}
          <fieldset disabled={!canManage} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Monthly salary is divided by"
                htmlFor="tp-basis"
                helper="Used to work out the cost of an unpaid day"
              >
                <NativeSelect
                  id="tp-basis"
                  value={dayBasis}
                  onChange={(e) => setDayBasis(e.target.value as DayBasis)}
                >
                  <option value="calendar_days">Days in the month</option>
                  <option value="working_days">Working days only</option>
                </NativeSelect>
              </Field>
              <Field label="Leave year starts in" htmlFor="tp-leave-start">
                <NativeSelect
                  id="tp-leave-start"
                  value={leaveStart}
                  onChange={(e) => setLeaveStart(Number(e.target.value))}
                >
                  {MONTHS.map((name, idx) => (
                    <option key={name} value={idx + 1}>
                      {name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Advances are taken back" htmlFor="tp-plan">
                <NativeSelect
                  id="tp-plan"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value as RecoveryPlan)}
                >
                  {(["emi", "next_month", "same_month"] as RecoveryPlan[]).map((p) => (
                    <option key={p} value={p}>
                      {RECOVERY_PLAN_LABELS[p]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              {plan === "emi" ? (
                <Field label="Usual number of instalments" htmlFor="tp-emi">
                  <Input
                    id="tp-emi"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={36}
                    value={emi}
                    onChange={(e) => setEmi(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>
            <ToggleRow
              label="Pay daily-rate people for holidays"
              helper="Monthly salaries always include holidays."
              checked={paidHolidays}
              onChange={setPaidHolidays}
            />
            <ToggleRow
              label="Show pay in the portal"
              helper="Team members see their own earnings, payments and advances."
              checked={showPay}
              onChange={setShowPay}
            />
            <ToggleRow
              label="Allow leave requests from the portal"
              helper="Requests wait for you on the Leave tab."
              checked={allowLeave}
              onChange={setAllowLeave}
            />
          </fieldset>
          {canManage ? (
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? (
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                ) : null}
                Save pay settings
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

export function SettingsTab({ canManage }: { canManage: boolean }) {
  const policy = useTeamPayPolicy();
  if (policy.isError) {
    return (
      <ErrorBanner
        message={errorMessage(policy.error, "Couldn't load settings.")}
        onRetry={() => policy.refetch()}
      />
    );
  }
  if (!policy.data) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            className="bg-muted/50 h-96 rounded-xl motion-safe:animate-pulse"
          />
        ))}
      </div>
    );
  }
  const key = policy.data.updated_at ?? "initial";
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <AttendanceCard key={`att-${key}`} policy={policy.data} canManage={canManage} />
      <PayCard key={`pay-${key}`} policy={policy.data} canManage={canManage} />
    </div>
  );
}
